import { type FC, useContext, useEffect, useState } from 'react';
import useMeasure from 'react-use-measure';
import { type ResourceTypeText } from '@common/types/resource';
import cn from 'classnames';
import { cubicBezier, useAnimate } from 'framer-motion';
import mergeRefs from 'merge-refs';
import { v4 as uuidv4 } from 'uuid';

import { largeResourceIcons } from '@/components/helpers/imagesImports';
import { ClaimAnimationContext } from '@/context/ClaimAnimationContext';

import { MotionPicture } from '../Picture/Picture';

import { easeOutTransition, getFirstStepConfig } from './config';
import { calcDiff } from './lib';

interface ClaimResourceAnimationProps {
  resourceKind: ResourceTypeText;
  className?: string;
}

const ClaimResourceAnimation: FC<ClaimResourceAnimationProps> = ({
  resourceKind,
}) => {
  const [animationWraper, wrapper] = useMeasure();
  const [refFirst, firstMeasure] = useMeasure();
  const [firstScope, firstAnimate] = useAnimate();
  const [secondScope, secondAnimate] = useAnimate();
  const [thirdScope, thirdAnimate] = useAnimate();
  const {
    tapElemMeasure,
    isAnimationStart,
    setIsAnimationStart,
    activeNavMeasure,
    deltaY,
  } = useContext(ClaimAnimationContext);
  const [uniqId, setUniqId] = useState<Record<number, string>>({
    0: uuidv4(),
    1: uuidv4(),
    2: uuidv4(),
  });

  useEffect(() => {
    const difference = calcDiff({ from: firstMeasure, to: tapElemMeasure });

    const animation = async (): Promise<void> => {
      if (!isAnimationStart || !activeNavMeasure) {
        return;
      }
      const firstStepAnimationConfig = getFirstStepConfig(difference);

      if (!firstScope.current) return;
      await firstAnimate(
        firstScope.current,
        { ...firstStepAnimationConfig, scale: 1.75 },
        {
          ease: cubicBezier(0.36, 0, 0.66, -0.56),
          duration: 0.4,
        },
      );

      if (!firstScope.current || !secondScope.current) return;
      await Promise.all([
        firstAnimate(
          firstScope.current,
          { ...firstStepAnimationConfig, scale: 2.125 },
          easeOutTransition,
        ),
        secondAnimate(
          secondScope.current,
          firstStepAnimationConfig,
          easeOutTransition,
        ),
      ]);
      setUniqId((state) => ({ ...state, 1: uuidv4() }));

      if (!firstScope.current || !thirdScope.current) return;
      await Promise.all([
        firstAnimate(
          firstScope.current,
          { ...firstStepAnimationConfig, scale: 2.5 },
          easeOutTransition,
        ),
        thirdAnimate(
          thirdScope.current,
          firstStepAnimationConfig,
          easeOutTransition,
        ),
      ]);
      setUniqId((state) => ({ ...state, 2: uuidv4() }));

      if (!firstScope.current) return;
      await Promise.all([
        firstAnimate(
          firstScope.current,
          {
            ...firstStepAnimationConfig,
            scale: 3.025,
            filter: 'drop-shadow(0px 0px 5px #82E024)',
          },
          {
            ease: 'easeIn',
            delay: 0.1,
            duration: 0.2,
          },
        ),
      ]);

      if (!firstScope.current) return;
      await firstAnimate(
        firstScope.current,
        {
          x: activeNavMeasure.x - wrapper.x - deltaY.current,
          y: activeNavMeasure.y - wrapper.y,
          scale: 1,
          filter: 'drop-shadow(0px 0px 5px #82E024)',
        },
        {
          ease: 'easeIn',
          duration: 0.4,
        },
      );

      if (!firstScope.current) return;
      await firstAnimate(
        firstScope.current,
        {
          x: activeNavMeasure.x - wrapper.x - deltaY.current,
          y: activeNavMeasure.y - wrapper.y,
          scale: 1,
          opacity: 0,
          filter: 'drop-shadow(0px 0px 0px rgba(0,0,0,0))',
        },
        {
          ease: 'easeOut',
          duration: 0.2,
        },
      );
      setUniqId({ 0: uuidv4(), 1: uuidv4(), 2: uuidv4() });
      setIsAnimationStart(false);
    };

    if (isAnimationStart && activeNavMeasure) {
      animation();
    }
  }, [
    isAnimationStart,
    firstMeasure,
    tapElemMeasure,
    activeNavMeasure,
    wrapper,
  ]);

  useEffect(() => {
    setIsAnimationStart(false);
  }, [resourceKind]);

  const img = largeResourceIcons[resourceKind];

  return (
    <div
      ref={animationWraper}
      className="absolute top-full left-1/2 -translate-x-1/2 w-20 h-20"
    >
      {Array.from({ length: 3 }).map((_, index) => {
        const ref =
          index === 0
            ? mergeRefs(firstScope, refFirst)
            : index === 1
              ? secondScope
              : thirdScope;

        const keyId = index in uniqId ? uniqId[index] : index;

        return (
          <MotionPicture
            key={`${resourceKind}-${keyId}`}
            ref={ref}
            initial={{
              x: 0,
              y: 0,
              opacity: 0,
              scale: 1,
            }}
            className={cn('fixed w-20 h-20 z-[1]', index === 0 && 'z-[3]')}
            img={img?.png}
            webp={img?.webp}
            alt={resourceKind}
          />
        );
      })}
    </div>
  );
};

export { ClaimResourceAnimation };
